import { useMemo, useRef, useState } from "react";
import { AlertCircle, Loader2, Paperclip, X } from "lucide-react";
import { uploadAttachment } from "@/api/attachments";
import type { Attachment } from "@/api/types";
import { AttachmentList, type AttachmentParentType } from "./AttachmentList";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_FILE_BYTES,
  formatFileSize,
} from "./constants";

/** A pending (uploaded-but-not-yet-persisted) attachment in the local queue. */
export interface PendingAttachment {
  id: string;
  filename: string;
  size: number;
  mime: string;
}

/**
 * TASK-ATT v1.3 (W12) — reusable upload control for Task + CCI.
 *
 * UPLOAD is sequential (for-loop await — base64 one-shot, mobile memory R5).
 * Each picked file: ≤20MB guard + total ≤10 cap (saved + pending). Upload state
 * is LOCAL; the surface owns persistence.
 *
 * onChange fires whenever the bound set changes with:
 *   - attachmentsIds: the FULL merged id list (saved kept + pending). 🔴 Send
 *     THIS exact array in the mutation payload — a partial list UNLINKS omitted.
 *   - pending: the local pending metadata (so the surface can show optimistic
 *     rows / persist names if it wants).
 * Remove of a SAVED attachment is delegated up via onRemoveSaved (drop the id
 * from the submitted list = unlink). Remove of a PENDING item is local.
 *
 * `disabled` hides the picker + per-row remove (lock: completed/canceled Task,
 * C+ post-submit). Saved attachments stay visible (view/download) via the list.
 */
export function AttachmentUpload({
  parentType,
  parentId,
  saved,
  pending,
  onChange,
  onRemoveSaved,
  disabled = false,
  label = "Вкладення",
}: {
  parentType: AttachmentParentType;
  /**
   * Parent id for building secured download URLs of SAVED attachments. Empty
   * string when the parent does not exist yet (create flow) — saved is then [].
   */
  parentId: string;
  /** Already-persisted attachments (from the detail response). */
  saved: Attachment[];
  /** Local pending queue (lifted to the surface; pass back what onChange gives). */
  pending: PendingAttachment[];
  /** Fires with the FULL merged id list + the new pending queue. */
  onChange: (next: { attachmentsIds: string[]; pending: PendingAttachment[] }) => void;
  /** Remove a SAVED attachment (surface drops its id from the bound list). */
  onRemoveSaved?: (id: string) => void;
  /** Lock — hides picker + remove controls (view/download only). */
  disabled?: boolean;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = saved.length + pending.length;
  const atCap = total >= MAX_ATTACHMENTS;

  const savedIds = useMemo(() => saved.map((a) => a.id), [saved]);

  const emit = (nextPending: PendingAttachment[]) => {
    onChange({
      attachmentsIds: [...savedIds, ...nextPending.map((p) => p.id)],
      pending: nextPending,
    });
  };

  const pickFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setUploading(true);
    setError(null);
    let acc = pending;
    try {
      // SEQUENTIAL (R5): for-loop await, NOT Promise.all.
      for (const f of Array.from(list)) {
        if (saved.length + acc.length >= MAX_ATTACHMENTS) {
          setError(`Максимум ${MAX_ATTACHMENTS} файлів`);
          break;
        }
        if (f.size > MAX_FILE_BYTES) {
          setError(`«${f.name}» перевищує ${formatFileSize(MAX_FILE_BYTES)}`);
          continue;
        }
        const up = await uploadAttachment(f, parentType, "attachments");
        acc = [
          ...acc,
          { id: up.id, filename: f.name, size: f.size, mime: f.type || up.type },
        ];
        emit(acc);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося завантажити файл");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removePending = (id: string) => {
    emit(pending.filter((p) => p.id !== id));
  };

  return (
    <div className="space-y-2">
      {label && (
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
      )}
      {/* Saved attachments — always view/download; remove only when editable. */}
      {parentId && saved.length > 0 && (
        <AttachmentList
          parentType={parentType}
          parentId={parentId}
          attachments={saved}
          onRemove={!disabled ? onRemoveSaved : undefined}
        />
      )}

      {/* Pending (uploaded this session, not yet persisted). */}
      {pending.length > 0 && (
        <ul className="space-y-1">
          {pending.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5 text-[12.5px]"
            >
              <span className="truncate">
                {p.filename}{" "}
                <span className="text-muted-foreground">({formatFileSize(p.size)})</span>
              </span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Прибрати ${p.filename}`}
                  className="ml-2 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removePending(p.id)}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && (
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            capture="environment"
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            onChange={(e) => void pickFiles(e.target.files)}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || atCap}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md border px-3 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
            Додати файл
          </button>
          <p className="mt-1 text-[11px] text-muted-foreground">
            До {MAX_ATTACHMENTS} файлів, ≤{formatFileSize(MAX_FILE_BYTES)} кожен:
            JPEG, PNG, WebP, HEIC, PDF, DOCX, XLSX, PPTX.
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
        </p>
      )}
    </div>
  );
}
